import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
}

export interface AuthCompany {
  id: number;
  name: string;
  role: string;
}

interface AuthState {
  user: AuthUser | null;
  company: AuthCompany | null;
  permissions: Record<string, unknown>;
  _isAuthenticated: boolean;

  setAuth: (user: AuthUser, company: AuthCompany, permissions: Record<string, unknown>) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
}

// ---------------------------------------------------------------------------
// CSRF helper
// ---------------------------------------------------------------------------

function getCsrfToken(): string {
  const match = document.cookie.match(/csrf_access_token=([^;]+)/);
  return match ? match[1] : '';
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      company: null,
      permissions: {},
      _isAuthenticated: false,

      setAuth: (user, company, permissions) =>
        set({ user, company, permissions, _isAuthenticated: true }),

      logout: () => {
        // Best-effort server logout
        fetch('/api/auth/logout', {
          method: 'POST',
          credentials: 'include',
          headers: { 'X-CSRF-TOKEN': getCsrfToken() },
        }).catch(() => {});
        set({ user: null, company: null, permissions: {}, _isAuthenticated: false });
      },

      isAuthenticated: () => get()._isAuthenticated,
    }),
    {
      name: 'sideview-caisse-auth',
      partialize: (state) => ({
        user: state.user,
        company: state.company,
        permissions: state.permissions,
        _isAuthenticated: state._isAuthenticated,
      }),
    },
  ),
);
